import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import article from "./Content";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";
import './PagesCss/StockProfitCalculator.css';

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
);

const DividendProfitCalculator = () => {
  const content = article.find((item) => item.id === 3);
  const [mode, setMode] = useState("investor");
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [riskLevel, setRiskLevel] = useState("medium");
  const [notifications, setNotifications] = useState([]);

  const [inputs, setInputs] = useState({
    buyPrice: "50.00",
    shares: "100",
    annualDividend: "2.50",
    frequency: "quarterly",
    dividendTax: "15.00",
    currentPrice: "55.00",
    growthRate: "5.00",
    holdingPeriod: "10",
    reinvest: true,
    conversionRate: "1.00",
    otherCharges: "0.00",
    dripDiscount: "0.00",
    monthlyContribution: "100",
    dividendFrequency: "quarterly",
    taxEfficiency: "standard",
    inflationRate: "2.50",
    targetYield: "4.00",
    initialInvestment: "5000",
    annualContribution: "1200",
    portfolioValue: "10000"
  });

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);
  const chartRef = useRef();

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculateDividendProfit();
    }
  }, [inputs, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setInputs(prev => ({ 
      ...prev, 
      [name]: type === "checkbox" ? checked : value 
    }));
  };

  const calculateDividendProfit = () => {
    const {
      buyPrice, shares, annualDividend, frequency, dividendTax,
      currentPrice, growthRate, holdingPeriod, reinvest, conversionRate,
      otherCharges, dripDiscount, monthlyContribution, inflationRate,
      initialInvestment, annualContribution
    } = inputs;

    if (!buyPrice || !shares || !annualDividend) {
      return;
    }

    const buy = parseFloat(buyPrice);
    const numShares = parseFloat(shares);
    const divPerShare = parseFloat(annualDividend);
    const tax = (parseFloat(dividendTax) || 0) / 100;
    const growth = (parseFloat(growthRate) || 0) / 100;
    const years = parseFloat(holdingPeriod) || 1;
    const convRate = parseFloat(conversionRate) || 1;
    const charges = parseFloat(otherCharges) || 0;
    const dripDisc = (parseFloat(dripDiscount) || 0) / 100;
    const monthlyContrib = parseFloat(monthlyContribution) || 0;
    const inflation = (parseFloat(inflationRate) || 0) / 100;
    const initialInv = parseFloat(initialInvestment) || (buy * numShares);
    const annualContrib = parseFloat(annualContribution) || 0;
    const currPrice = parseFloat(currentPrice) || buy;

    // Adjust dividend based on frequency
    const freqMultiplier = {
      monthly: 12,
      quarterly: 4,
      semiAnnual: 2,
      yearly: 1
    }[frequency] || 1;

    // Calculate annual dividend income
    const annualDividendIncome = divPerShare * numShares * freqMultiplier;
    const netAnnualDividend = annualDividendIncome * (1 - tax) * convRate;

    // Calculate growth over holding period
    const projectionData = [];
    let totalShares = numShares;
    let totalDividendIncome = 0;
    let totalContributions = initialInv;
    let portfolioValue = initialInv;

    for (let year = 0; year <= years; year++) {
      if (year > 0) {
        // Calculate annual dividend
        const annualDiv = divPerShare * totalShares * freqMultiplier;
        const netDiv = annualDiv * (1 - tax) * convRate;
        
        if (reinvest) {
          // DRIP calculation with discount
          const sharePrice = currPrice * Math.pow(1 + growth, year);
          const sharesBought = (netDiv * (1 + dripDisc)) / sharePrice;
          totalShares += sharesBought;
        }
        
        totalDividendIncome += netDiv;
        
        // Add annual contributions
        totalShares += annualContrib / (currPrice * Math.pow(1 + growth, year));
        totalContributions += annualContrib;
      }

      // Calculate portfolio value
      portfolioValue = totalShares * (currPrice * Math.pow(1 + growth, year));
      
      projectionData.push({
        year,
        dividendIncome: totalDividendIncome,
        portfolioValue,
        totalShares,
        annualYield: (divPerShare * freqMultiplier) / (currPrice * Math.pow(1 + growth, year)) * 100
      });
    }

    // Calculate key metrics
    const currentYield = (divPerShare * freqMultiplier / currPrice) * 100;
    const costYield = (divPerShare * freqMultiplier / buy) * 100;
    const unrealizedGain = (currPrice - buy) * totalShares;
    const totalReturn = unrealizedGain + totalDividendIncome;
    const annualizedReturn = (Math.pow((portfolioValue + totalDividendIncome) / initialInv, 1/years) - 1) * 100;

    // Inflation-adjusted calculations
    const realDividendIncome = totalDividendIncome / Math.pow(1 + inflation, years);
    const realTotalReturn = totalReturn / Math.pow(1 + inflation, years);

    const resultData = {
      totalDividendIncome,
      unrealizedGain,
      portfolioValue,
      totalShares,
      currentYield,
      costYield,
      totalReturn,
      annualizedReturn,
      realDividendIncome,
      realTotalReturn,
      projectionData,
      yearsHeld: years,
      annualDividendIncome: netAnnualDividend,
      monthlyDividendIncome: netAnnualDividend / 12,
      dividendGrowth: growth * 100
    };

    setResult(resultData);
  };

  const calculateYieldTarget = () => {
    const { targetYield, portfolioValue, annualDividend } = inputs;
    
    if (!targetYield || !portfolioValue) return null;

    const target = parseFloat(targetYield) / 100;
    const portfolio = parseFloat(portfolioValue);
    const currentDiv = parseFloat(annualDividend) || 0;

    const requiredAnnualDividend = portfolio * target;
    const additionalInvestment = (requiredAnnualDividend - currentDiv) / target;
    const sharesToBuy = additionalInvestment / (parseFloat(inputs.currentPrice) || parseFloat(inputs.buyPrice));

    return {
      requiredAnnualDividend,
      additionalInvestment,
      sharesToBuy: Math.ceil(sharesToBuy),
      currentYield: (currentDiv / portfolio) * 100
    };
  };

  const calculateTaxEfficiency = () => {
    const { dividendTax, annualDividend, shares, frequency, taxEfficiency } = inputs;
    
    const taxRate = parseFloat(dividendTax) || 0;
    const divPerShare = parseFloat(annualDividend) || 0;
    const numShares = parseFloat(shares) || 0;
    const freqMultiplier = {
      monthly: 12,
      quarterly: 4,
      semiAnnual: 2,
      yearly: 1
    }[frequency] || 1;

    const annualIncome = divPerShare * numShares * freqMultiplier;
    
    let taxSavings = 0;
    switch(taxEfficiency) {
      case "taxFree":
        taxSavings = annualIncome * (taxRate / 100);
        break;
      case "taxDeferred":
        taxSavings = annualIncome * (taxRate / 100) * 0.75;
        break;
      case "qualified":
        taxSavings = annualIncome * (taxRate / 100) * 0.5;
        break;
      default:
        taxSavings = 0;
    }

    return {
      annualIncome,
      taxPaid: annualIncome * (taxRate / 100),
      taxSavings,
      netIncome: annualIncome - (annualIncome * (taxRate / 100)) + taxSavings
    };
  };

  const resetCalculator = () => {
    setInputs({
      buyPrice: "50.00",
      shares: "100",
      annualDividend: "2.50",
      frequency: "quarterly",
      dividendTax: "15.00",
      currentPrice: "55.00",
      growthRate: "5.00",
      holdingPeriod: "10",
      reinvest: true,
      conversionRate: "1.00",
      otherCharges: "0.00",
      dripDiscount: "0.00",
      monthlyContribution: "100",
      dividendFrequency: "quarterly",
      taxEfficiency: "standard",
      inflationRate: "2.50",
      targetYield: "4.00",
      initialInvestment: "5000",
      annualContribution: "1200",
      portfolioValue: "10000"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Metric,Value",
      `Total Dividend Income,${result.totalDividendIncome.toFixed(2)}`,
      `Portfolio Value,${result.portfolioValue.toFixed(2)}`,
      `Current Yield,${result.currentYield.toFixed(2)}%`,
      `Cost Yield,${result.costYield.toFixed(2)}%`,
      `Total Return,${result.totalReturn.toFixed(2)}`,
      `Annualized Return,${result.annualizedReturn.toFixed(2)}%`,
      `Total Shares,${result.totalShares.toFixed(2)}`,
      `Years Held,${result.yearsHeld}`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dividend-calculation.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      ...result
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setResult(calc);
    setShowSaved(false);
    addNotification("Calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { growthRate: "3.00", targetYield: "3.00", dividendTax: "15.00" },
      moderate: { growthRate: "5.00", targetYield: "4.00", dividendTax: "20.00" },
      aggressive: { growthRate: "8.00", targetYield: "6.00", dividendTax: "25.00" }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset} preset applied`, "info");
  };

  // Chart Data
  const incomeChartData = result ? {
    labels: ["Dividend Income", "Capital Gains", "Total Contributions"],
    datasets: [{
      data: [
        result.totalDividendIncome, 
        result.unrealizedGain,
        parseFloat(inputs.initialInvestment) + (parseFloat(inputs.annualContribution) * result.yearsHeld)
      ],
      backgroundColor: ["#28A745", "#19692C", "#FFD93D"],
      borderWidth: 2,
      borderColor: darkMode ? "#1A202C" : "#FFFFFF",
      hoverOffset: 15
    }],
  } : null;

  const projectionData = result?.projectionData;
  const projectionChartData = projectionData ? {
    labels: projectionData.map(d => `Year ${d.year}`),
    datasets: [
      {
        label: 'Portfolio Value',
        data: projectionData.map(d => d.portfolioValue),
        borderColor: "#19692C",
        backgroundColor: "rgba(25, 105, 44, 0.1)",
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "#19692C",
        pointBorderColor: "#FFFFFF",
        pointBorderWidth: 2
      },
      {
        label: 'Dividend Income',
        data: projectionData.map(d => d.dividendIncome),
        borderColor: "#28A745",
        backgroundColor: "rgba(40, 167, 69, 0.1)",
        fill: true,
        tension: 0.4,
        borderDash: [5, 5]
      }
    ]
  } : null;

  const yieldTargetData = calculateYieldTarget();
  const taxEfficiencyData = calculateTaxEfficiency();

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Dividend Profit Calculator</h1>
        </div>
        <p>Advanced dividend income calculations, DRIP projections, and yield optimization</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">Dividend Portfolio</div>
                    <div className="saved-details">
                      <span className={`profit ${calc.totalDividendIncome >= 0 ? 'positive' : 'negative'}`}>
                        ${calc.totalDividendIncome.toFixed(2)}
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Dividend Calculator'}
              </button>
              <button 
                className={`tab ${activeTab === "yield" ? "active" : ""}`}
                onClick={() => setActiveTab("yield")}
              >
                <i className="fas fa-bullseye"></i>
                {isMobile ? 'Yield' : 'Yield Target'}
              </button>
              <button 
                className={`tab ${activeTab === "tax" ? "active" : ""}`}
                onClick={() => setActiveTab("tax")}
              >
                <i className="fas fa-file-invoice-dollar"></i>
                {isMobile ? 'Tax' : 'Tax Efficiency'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Investment Strategy:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
            </div>
          </div>

          {/* Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-chart-bar"></i>
                    Investment Details
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Buy Price</label>
                      <input 
                        type="number" 
                        name="buyPrice" 
                        value={inputs.buyPrice} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-layer-group"></i> Shares</label>
                      <input 
                        type="number" 
                        name="shares" 
                        value={inputs.shares} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Current Price</label>
                      <input 
                        type="number" 
                        name="currentPrice" 
                        value={inputs.currentPrice} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-money-bill-wave"></i> Annual Dividend</label>
                      <input 
                        type="number" 
                        name="annualDividend" 
                        value={inputs.annualDividend} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar"></i> Dividend Frequency</label>
                      <select name="frequency" value={inputs.frequency} onChange={handleChange}>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="semiAnnual">Semi-Annual</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Dividend Tax (%)</label>
                      <input 
                        type="number" 
                        name="dividendTax" 
                        value={inputs.dividendTax} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                  </div>

                  {/* Advanced Inputs */}
                  <h3>
                    <i className="fas fa-sliders-h"></i>
                    Growth & Reinvestment
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-line"></i> Dividend Growth Rate (%)</label>
                      <input 
                        type="number" 
                        name="growthRate" 
                        value={inputs.growthRate} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-clock"></i> Holding Period (Years)</label>
                      <input 
                        type="number" 
                        name="holdingPeriod" 
                        value={inputs.holdingPeriod} 
                        onChange={handleChange}
                        placeholder="10"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrows-repeat"></i> Currency Rate</label>
                      <input 
                        type="number" 
                        name="conversionRate" 
                        value={inputs.conversionRate} 
                        onChange={handleChange}
                        placeholder="1.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> DRIP Discount (%)</label>
                      <input 
                        type="number" 
                        name="dripDiscount" 
                        value={inputs.dripDiscount} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Annual Contribution</label>
                      <input 
                        type="number" 
                        name="annualContribution" 
                        value={inputs.annualContribution} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                  </div>

                  <div className="input-row">
                    <div className="checkbox-field">
                      <label  className="checkbox-label">
                        <input 
                          type="checkbox" 
                          name="reinvest" 
                          checked={inputs.reinvest} 
                          onChange={handleChange}
                        />
                        <i className="fa-solid fa-arrows-rotate"></i>
                        Reinvest Dividends (DRIP)
                      </label>
                    </div>
                  </div>

                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Other Charges</label>
                      <input 
                        type="number" 
                        name="otherCharges" 
                        value={inputs.otherCharges} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-line"></i> Inflation Rate (%)</label>
                      <input 
                        type="number" 
                        name="inflationRate" 
                        value={inputs.inflationRate} 
                        onChange={handleChange}
                        placeholder="2.50"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Yield Target Tab */}
          {activeTab === "yield" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-bullseye"></i>
                  Yield Target Calculator
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-percentage"></i> Target Yield (%)</label>
                    <input 
                      type="number" 
                      name="targetYield" 
                      value={inputs.targetYield} 
                      onChange={handleChange}
                      placeholder="4.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-dollar-sign"></i> Portfolio Value</label>
                    <input 
                      type="number" 
                      name="portfolioValue" 
                      value={inputs.portfolioValue} 
                      onChange={handleChange}
                      placeholder="10000"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-money-bill-wave"></i> Current Annual Dividend</label>
                    <input 
                      type="number" 
                      name="annualDividend" 
                      value={inputs.annualDividend} 
                      onChange={handleChange}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tax Efficiency Tab */}
          {activeTab === "tax" && (
            <div className="risk-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-file-invoice-dollar"></i>
                  Tax Efficiency Calculator
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-percentage"></i> Dividend Tax Rate (%)</label>
                    <input 
                      type="number" 
                      name="dividendTax" 
                      value={inputs.dividendTax} 
                      onChange={handleChange}
                      placeholder="15.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-money-bill-wave"></i> Dividend per Share</label>
                    <input 
                      type="number" 
                      name="annualDividend" 
                      value={inputs.annualDividend} 
                      onChange={handleChange}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-layer-group"></i> Number of Shares</label>
                    <input 
                      type="number" 
                      name="shares" 
                      value={inputs.shares} 
                      onChange={handleChange}
                      placeholder="100"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-calendar"></i> Dividend Frequency</label>
                    <select name="frequency" value={inputs.frequency} onChange={handleChange}>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="semiAnnual">Semi-Annual</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-shield"></i> Tax Efficiency Type</label>
                    <select name="taxEfficiency" value={inputs.taxEfficiency} onChange={handleChange}>
                      <option value="standard">Standard</option>
                      <option value="qualified">Qualified Dividends</option>
                      <option value="taxDeferred">Tax-Deferred Account</option>
                      <option value="taxFree">Tax-Free Account</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className={`history-profit ${calc.totalDividendIncome >= 0 ? 'positive' : 'negative'}`}>
                          <i className="fas fa-coins"></i>
                          ${calc.totalDividendIncome.toFixed(2)}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-percentage"></i> Yield: {calc.currentYield.toFixed(2)}%</span>
                        <span><i className="fas fa-chart-line"></i> Total Return: ${calc.totalReturn.toFixed(2)}</span>
                        <span><i className="fas fa-calendar"></i> Years: {calc.yearsHeld}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "yield" || activeTab === "tax") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-coins"></i>
                    Dividend Income Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value positive">
                          ${result.totalDividendIncome.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-money-bill-wave"></i>
                          Total Dividend Income
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.currentYield.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-percentage"></i>
                          Current Yield
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.costYield.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-percentage"></i>
                          Cost Yield
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.annualizedReturn.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-chart-line"></i>
                          Annualized Return
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-piggy-bank"></i> Portfolio Value:</span>
                        <span>${result.portfolioValue.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-chart-line"></i> Unrealized Gain:</span>
                        <span>${result.unrealizedGain.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-coins"></i> Total Return:</span>
                        <span>${result.totalReturn.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-layer-group"></i> Total Shares:</span>
                        <span>{result.totalShares.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-calendar"></i> Annual Dividend Income:</span>
                        <span>${result.annualDividendIncome.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-moon"></i> Monthly Dividend Income:</span>
                        <span>${result.monthlyDividendIncome.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Real Returns */}
                    <div className="risk-summary">
                      <h4>Inflation-Adjusted Returns</h4>
                      <div className="risk-cards">
                        <div className="risk-card">
                          <div className="risk-value">
                            ${result.realDividendIncome.toFixed(2)}
                          </div>
                          <div className="risk-label">Real Dividend Income</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value">
                            ${result.realTotalReturn.toFixed(2)}
                          </div>
                          <div className="risk-label">Real Total Return</div>
                        </div>
                      </div>
                    </div>

                     {/* Projection Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-line"></i>
                        {inputs.reinvest ? 'DRIP Projection' : 'Income Projection'}
                      </h3>
                      <Line 
                        data={projectionChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: true,
                              position: 'top'
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                callback: function(value) {
                                  return '$' + value.toLocaleString();
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div><br/>

                    {/* Charts */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Income Distribution
                      </h3>
                      <Doughnut 
                        data={incomeChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toFixed(2)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-calculator fa-3x"></i>
                    <p>Enter your dividend details to see calculations</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "yield" && yieldTargetData && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-bullseye"></i>
                  Yield Target Analysis
                </h3>
                <div className="yield-target-results">
                  <div className="results-grid">
                    <div className="result-card highlight">
                      <div className="result-value">
                        ${yieldTargetData.requiredAnnualDividend.toFixed(2)}
                      </div>
                      <div className="result-label">
                        Required Annual Dividend
                      </div>
                    </div>
                    <div className="result-card">
                      <div className="result-value">
                        {yieldTargetData.currentYield.toFixed(2)}%
                      </div>
                      <div className="result-label">
                        Current Yield
                      </div>
                    </div>
                    <div className="result-card">
                      <div className="result-value">
                        ${yieldTargetData.additionalInvestment.toFixed(2)}
                      </div>
                      <div className="result-label">
                        Additional Investment Needed
                      </div>
                    </div>
                    <div className="result-card">
                      <div className="result-value">
                        {yieldTargetData.sharesToBuy}
                      </div>
                      <div className="result-label">
                        Shares to Buy
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "tax" && taxEfficiencyData && (
              <div className="risk-results-panel">
                <h3>
                  <i className="fas fa-file-invoice-dollar"></i>
                  Tax Efficiency Analysis
                </h3><br/>
                <div className="tax-results">
                  <div className="results-grid">
                    <div className="result-card highlight">
                      <div className="result-value">
                        ${taxEfficiencyData.netIncome.toFixed(2)}
                      </div>
                      <div className="result-label">
                        Net Dividend Income
                      </div>
                    </div>
                    <div className="result-card">
                      <div className="result-value">
                        ${taxEfficiencyData.taxPaid.toFixed(2)}
                      </div>
                      <div className="result-label">
                        Tax Paid
                      </div>
                    </div>
                    <div className="result-card">
                      <div className="result-value">
                        ${taxEfficiencyData.taxSavings.toFixed(2)}
                      </div>
                      <div className="result-label">
                        Tax Savings
                      </div>
                    </div>
                    <div className="result-card">
                      <div className="result-value">
                        ${taxEfficiencyData.annualIncome.toFixed(2)}
                      </div>
                      <div className="result-label">
                        Gross Income
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default DividendProfitCalculator;